// _early-fetch.js — D-088 cl.1 Slice 0 (coord ASK-178 §3 item 2): THE CRITICAL PATH STARTS AT PARSE. Measured on the hub
// (hub-841c5db, 2026-11-02 12:18Z, evidence/ASK-178/client-5l/): the boot (596 KB) was requested only after Firebase auth's
// token refresh (1.84 s), the registry read only after the authority read (3.28 s), the decoders + the hub chunks only when
// the first parse needed them — a chain of round trips with an idle network between them (Unity Addressables / Lighthouse
// "critical request chain": start every request the first frame needs as early as its URL is known).
//
// Two halves:
//   earlyFetchHead(cfg)  BUILD TIME (vite.config.ts → index.html only): an inline script that starts the boot fetch (cache-
//                        busted, `cache: "no-store"` — the boot is served max-age=3600) and, when the URL names the hub, the
//                        public registry read, and adds `<link rel=preload|modulepreload fetchpriority=high>` for the
//                        decoders + the hub's chunks. Each early fetch is kept ONCE on `window.__ggEarly[key]`.
//   takeEarly(key, url)  RUN TIME: the consumer takes it once, only when the URL is exactly the one it would have fetched
//                        (a different boot object / gateway → null → its own fetch, unchanged). A shop URL starts the boot
//                        only: the shop path is untouched.

/** The URL names the hub: no `shop=` (the default landing is the hub) or `shop=galleria-hub`. PURE. */
export function urlNamesHub(search) {
	const q = String(search || "");
	return !/[?&]shop=/.test(q) || /[?&]shop=galleria-hub(?:[&#]|$)/.test(q);
}

/** Take an early fetch ONCE → Promise<Response> | null. A rejected early fetch is the caller's to fall back from. */
export function takeEarly(key, url, w = typeof window !== "undefined" ? window : null) {
	const e = w?.__ggEarly?.[key];
	if (!e || e.taken || typeof e.url !== "string" || (url != null && e.url !== url)) return null;
	e.taken = true;
	return e.p;
}

/** An early fetch read as JSON, MEMOISED → Promise<{ ok, status, json? } | null>: several readers share the one response (the
 *  boot's loader + the hub prefetch; the registry's first hub entry + the hub prefetch). `take` marks it consumed for that
 *  consumer's single use (a second take → null: its own fetch, as before); a peek never consumes. `url` null = any. */
export function earlyJson(key, url = null, { take = false } = {}, w = typeof window !== "undefined" ? window : null) {
	const e = w?.__ggEarly?.[key];
	if (!e || typeof e.url !== "string" || (url != null && e.url !== url) || (take && e.taken)) return null;
	if (take) e.taken = true;
	return (e.j ||= Promise.resolve(e.p).then((r) => (r && r.ok ? r.json().then((json) => ({ ok: true, status: r.status, json })) : { ok: false, status: r?.status ?? null })).catch(() => null));
}

/** The street's bytes fetched ahead (`_hub-critical-path.js#prefetchHubBytes`), keyed by URL, each taken ONCE. */
export function putEarlyBytes(url, promise, w = typeof window !== "undefined" ? window : null) {
	if (!w || !url) return;
	(w.__ggEarlyBytes ||= new Map()).set(url, promise);
}
export function takeEarlyBytes(url, w = typeof window !== "undefined" ? window : null) {
	const m = w?.__ggEarlyBytes;
	if (!m || !m.has(url)) return null;
	const p = m.get(url);
	m.delete(url);
	return p;
}

/** A session that mounts the runtime leaves a hint (set at mount, cleared at sign-out): only then is the street fetched
 *  ahead of auth — a signed-out visitor (the gate) never downloads a street it cannot enter. */
export const SESSION_HINT_KEY = "gg_runtime_session";
export function sessionHint(set = undefined, w = typeof window !== "undefined" ? window : null) {
	try {
		if (set === true) w.localStorage.setItem(SESSION_HINT_KEY, "1");
		else if (set === false) w.localStorage.removeItem(SESSION_HINT_KEY);
		return w.localStorage.getItem(SESSION_HINT_KEY) === "1";
	} catch { return false; }
}

/** BUILD TIME → the inline `<script>` body for index.html's head. `bootUrl` / `doorSlotsUrl` are absolute; `preloads` =
 *  [{ rel: "preload" | "modulepreload", href, as? }] (hub only). Self-contained ES5, try-wrapped: a failure here costs
 *  nothing but the head start (every consumer falls back to its own fetch). */
export function earlyFetchHead({ bootUrl, doorSlotsUrl = null, preloads = [] }) {
	const J = JSON.stringify;
	return `(function(){try{
var w=window,d=document,q=location.search,E=w.__ggEarly={};
var hub=${urlNamesHub.toString().replace(/^function urlNamesHub/, "function")}(q);
var go=function(k,u,f,o){var p=fetch(f,o);p.catch(function(){});E[k]={url:u,p:p};};
var B=${J(bootUrl)};
go("boot",B,B+(B.indexOf("?")<0?"?":"&")+"cb="+Date.now(),{cache:"no-store",priority:"high"});
${doorSlotsUrl ? `if(hub)go("doorSlots",${J(doorSlotsUrl)},${J(doorSlotsUrl)},{priority:"high"});` : ""}
if(hub){var P=${J(preloads)};for(var i=0;i<P.length;i++){var l=d.createElement("link");l.rel=P[i].rel;l.href=P[i].href;if(P[i].as)l.as=P[i].as;l.crossOrigin="anonymous";l.setAttribute("fetchpriority","high");d.head.appendChild(l);}}
w.__ggEarlyHub=hub;
}catch(e){}})();`;
}
